import moveMailListRowsHelper from './helpers/moveMailListRowsHelper';
import showNotificationOnMove from './helpers/showNotificationOnMove';
import { updateCountForConversationMove } from './helpers/updateFolderCountForArchiveMailbox';
import datapoints from '../datapoints';
import isDeletedOrUnderDeletedItemsFolder from '../helpers/isDeletedOrUnderDeletedItemsFolder';
import shouldPerformRowRemovalInstantUpdate from '../helpers/shouldPerformRowRemovalInstantUpdate';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isFolderUnderArchiveRoot } from 'owa-mail-store';
import moveConversationService from 'owa-mail-store/lib/services/moveConversationService';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import { doesTableSupportUndo } from 'owa-mail-undo';
import * as undoActions from 'owa-mail-undo';
import {
    getApplyConversationActionContext,
    getRowIdsFromRowKeys,
    TableView,
} from 'owa-mail-list-store';

/**
 * The action worker which handles moving conversations.
 * @param rowKeys - the row keys of the rows that, need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
export default createLegacyActionWithDatapoint(
    'moveConversations',
    datapoints.moveConversation
)(async function moveConversations(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    actionSource: ActionSource
) {
    await moveMailListRowsHelper(
        rowKeys,
        destinationFolderId,
        originalTableView,
        moveConversationsInternal,
        actionSource
    );
});

/**
 * The move conversations internal method
 * @param rowKeys - the row keys of the rows that need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
function moveConversationsInternal(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView
): Promise<void> {
    const sourceFolderId = originalTableView.tableQuery.folderId;
    const conversationActionContexts = rowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, originalTableView.id)
    );
    const conversationIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableView.id);
    const allowUndo = doesTableSupportUndo(sourceFolderId, getMailboxInfo(originalTableView));

    /* If folder is under archive mailbox update folder count manually in local, since archive mbx doesn't have
    hierarchy notification unread count won't get updated instantly */
    isFolderUnderArchiveRoot(destinationFolderId) &&
        updateCountForConversationMove(rowKeys, originalTableView, destinationFolderId);

    if (
        shouldPerformRowRemovalInstantUpdate(
            originalTableView.tableQuery,
            isDeletedOrUnderDeletedItemsFolder(destinationFolderId)
        )
    ) {
        deleteConversationStoreUpdate(conversationIdStrings, originalTableView, 'Move');
    }

    // Make service request
    const moveConversationPromise = moveConversationService(
        conversationActionContexts,
        destinationFolderId,
        sourceFolderId
    );

    if (allowUndo) {
        undoActions.addActionToUndoStack(async () => {
            const response = await moveConversationPromise;
            if (response.ResponseClass == 'Success') {
                return moveConversationService(
                    conversationActionContexts,
                    sourceFolderId,
                    destinationFolderId
                );
            }
            return Promise.resolve();
        }, originalTableView.serverFolderId);
    }

    // Show notification bar immediately after client action
    showNotificationOnMove(conversationActionContexts.length, destinationFolderId, allowUndo);

    returnTopExecutingActionDatapoint().markUserPerceivedTime();

    return processApplyConversationActionResponseMessage(
        moveConversationPromise,
        rowKeys,
        sourceFolderId,
        originalTableView,
        true /* shouldRemoveRowsFromTombstone */
    );
}
